import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import Cookies from 'js-cookie';
import './CommonHome.css';
import Footer from '../Footer/Footer';

const slug = (section) => section?.RouteSlug || section?.Slug || section?.Sename;
const href = (section, fallback) => section?.Url || (section ? `/shopping/${slug(section)}/items` : fallback);
const values = (collection) => collection?.$values || [];
const formatPrice = (price) => `${new Intl.NumberFormat('ru-RU').format(price)} ₽`;

const CommonHome = ({ data }) => {
    const sections = values(data?.Sections || data);
    const highlights = values(data?.Highlights);
    const railRef = useRef(null);
    const dragRef = useRef({ active: false, dragged: false, startX: 0, startScroll: 0 });
    const suppressClickRef = useRef(false);
    const [railIndex, setRailIndex] = useState(0);
    useEffect(() => {
        const items = Array.from(document.querySelectorAll('[data-home-reveal]'));
        if (!('IntersectionObserver' in window)) {
            items.forEach((item) => item.classList.add('is-visible'));
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
        items.forEach((item) => observer.observe(item));
        return () => observer.disconnect();
    }, []);
    const women = sections.find((section) => slug(section) === 'women') || sections[0];
    const men = sections.find((section) => slug(section) === 'men') || sections[1];
    const remember = (section) => {
        const index = window.categories?.[slug(section)];
        if (index !== undefined) Cookies.set('selectedSection', String(index), { expires: 7 });
    };
    const moveRail = (direction = 1) => {
        const rail = railRef.current;
        if (!rail) return;
        const card = rail.querySelector('.home-product-rail__card');
        const step = (card?.getBoundingClientRect().width || 280) + 1;
        const visible = window.innerWidth <= 640 ? 1 : window.innerWidth <= 1100 ? 3 : 4;
        const lastIndex = Math.max(highlights.length - visible, 0);
        const nextIndex = direction > 0
            ? (railIndex >= lastIndex ? 0 : railIndex + 1)
            : (railIndex <= 0 ? lastIndex : railIndex - 1);
        setRailIndex(nextIndex);
        rail.scrollTo({ left: step * nextIndex, behavior: 'smooth' });
    };
    const beginRailDrag = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const rail = railRef.current;
        if (!rail) return;
        dragRef.current = { active: true, dragged: false, startX: event.clientX, startScroll: rail.scrollLeft };
        rail.setPointerCapture(event.pointerId);
    };
    const dragRail = (event) => {
        const rail = railRef.current;
        const drag = dragRef.current;
        if (!rail || !drag.active) return;
        const distance = event.clientX - drag.startX;
        if (Math.abs(distance) > 5) drag.dragged = true;
        rail.scrollLeft = drag.startScroll - distance;
    };
    const endRailDrag = (event) => {
        const rail = railRef.current;
        const drag = dragRef.current;
        if (!rail || !drag.active) return;
        drag.active = false;
        suppressClickRef.current = drag.dragged;
        if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
        const card = rail.querySelector('.home-product-rail__card');
        const step = (card?.getBoundingClientRect().width || 280) + 1;
        setRailIndex(Math.round(rail.scrollLeft / step));
        window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    };

    return (
        <>
        <main className="home-editorial">
            <section className="home-hero" aria-labelledby="home-title">
                <img className="home-hero__image" src="/img/home/merkuls-editorial-hero.png" alt="Современные женский и мужской образы" />
                <div className="home-hero__shade" />
                <div className="home-hero__content">
                    <p className="home-kicker">Новая редакция</p>
                    <h1 id="home-title">MERKULS</h1>
                    <p className="home-hero__copy">Коллекция вещей, выбранных за точность силуэта, материала и состояния.</p>
                    <div className="home-hero__actions">
                        <a href={href(women, '/shopping/women/items')} onClick={() => remember(women)}>Для неё <ArrowUpRight size={17} /></a>
                        <a href={href(men, '/shopping/men/items')} onClick={() => remember(men)}>Для него <ArrowUpRight size={17} /></a>
                    </div>
                </div>
            </section>

            <nav className="home-orbit" aria-label="Основные направления" data-home-reveal>
                <a href="/shopping/women/brands/items"><span>01</span>Бренды</a>
                <a href="/shopping/women/bags/items"><span>02</span>Сумки</a>
                <a href="/shopping/women/shoes/items"><span>03</span>Обувь</a>
                <a href="/shopping/women/accessories/items"><span>04</span>Аксессуары</a>
            </nav>

            <section className="home-statement" data-home-reveal>
                <p className="home-kicker">Кураторский каталог</p>
                <h2>Знакомые имена и редкие находки без визуального шума.</h2>
            </section>

            <section className="home-category-grid" aria-label="Избранные категории">
                <div className="home-category-grid__copy home-category-grid__copy--dark">
                    <p className="home-kicker">Женская коллекция</p>
                    <h2>Выразительные детали</h2>
                    <p>Сумки, украшения и аксессуары, которые точно завершают образ.</p>
                    <a href="/shopping/women/accessories/items">Аксессуары <ArrowUpRight size={17} /></a>
                </div>
                <a className="home-category-grid__image home-category-grid__image--wide" href="/shopping/women/bags/items">
                    <img src="/img/home/merkuls-accessories-editorial.png" alt="Сумка и украшения в редакционной композиции" loading="lazy" />
                    <span>Сумки <ArrowUpRight size={17} /></span>
                </a>
                <a className="home-category-grid__image home-category-grid__image--tall" href="/shopping/men/shoes/items">
                    <img src="/img/home/merkuls-footwear-editorial.png" alt="Мужская обувь в движении" loading="lazy" />
                    <span>Обувь <ArrowUpRight size={17} /></span>
                </a>
                <div className="home-category-grid__copy home-category-grid__copy--light">
                    <p className="home-kicker">Мужская коллекция</p>
                    <h2>Новая форма повседневного</h2>
                    <p>Чистые линии, точные пропорции и вещи, рассчитанные на движение.</p>
                    <a href="/shopping/men/clothing/items">Одежда <ArrowUpRight size={17} /></a>
                </div>
            </section>

            <section className="home-ticker" aria-label="Философия MERKULS">
                <div className="home-ticker__track" aria-hidden="true">
                    <span>ТОЧНОСТЬ ФОРМЫ</span><i>—</i><span>ВЕЩИ С ХАРАКТЕРОМ</span><i>—</i><span>ВНЕ ВРЕМЕНИ</span><i>—</i>
                    <span>ТОЧНОСТЬ ФОРМЫ</span><i>—</i><span>ВЕЩИ С ХАРАКТЕРОМ</span><i>—</i><span>ВНЕ ВРЕМЕНИ</span><i>—</i>
                </div>
            </section>

            {highlights.length > 0 && (
                <section className="home-highlights" aria-labelledby="home-highlights-title" data-home-reveal>
                    <div className="home-highlights__header">
                        <p className="home-kicker">Выбор MERKULS</p><h2 id="home-highlights-title">Хайлайты каталога</h2>
                    </div>
                    <div className="home-product-rail__stage">
                        <button className="home-product-rail__arrow home-product-rail__arrow--previous" type="button" onClick={() => moveRail(-1)} aria-label="Предыдущие товары"><ChevronLeft /></button>
                        <div
                            className="home-product-rail"
                            ref={railRef}
                            onPointerDown={beginRailDrag}
                            onPointerMove={dragRail}
                            onPointerUp={endRailDrag}
                            onPointerCancel={endRailDrag}
                            onClickCapture={(event) => { if (suppressClickRef.current) event.preventDefault(); }}
                        >
                        {highlights.map((product, index) => {
                            const images = values(product.ImagesPath);
                            const productHref = `/shopping/preorder/${product.PrimarySection || 'women'}/${product.Sename}`;
                            return (
                                <a className="home-product-rail__card" href={productHref} key={product.ProductId}>
                                    <div className="home-product-rail__image">
                                        <img className="home-product-rail__primary" src={images[0]?.URL} alt={product.Title} loading={index < 3 ? 'eager' : 'lazy'} />
                                        {images[1]?.URL && (
                                            <img
                                                className="home-product-rail__secondary"
                                                src={images[1].URL}
                                                alt=""
                                                loading="lazy"
                                                onLoad={(event) => {
                                                    event.currentTarget.classList.add('is-loaded');
                                                    event.currentTarget.previousElementSibling?.classList.add('has-loaded-alternate');
                                                }}
                                            />
                                        )}
                                    </div>
                                    <strong>{product.BrandName}</strong><span>{product.Title}</span><small>{formatPrice(product.Price)}</small>
                                </a>
                            );
                        })}
                        </div>
                        <button className="home-product-rail__arrow home-product-rail__arrow--next" type="button" onClick={() => moveRail(1)} aria-label="Следующие товары"><ChevronRight /></button>
                    </div>
                </section>
            )}

            <section className="home-directions" data-home-reveal>
                <div className="home-directions__media">
                    <img src="/img/home/merkuls-editorial-detail.png" alt="Детали современных образов" loading="lazy" />
                </div>
                <div className="home-directions__content">
                    <p className="home-kicker">Каталог</p>
                    <h2>Основные направления</h2>
                    <div className="home-directions__groups">
                        <div><h3>Для неё</h3><a href="/shopping/women/clothing/items">Одежда</a><a href="/shopping/women/bags/items">Сумки</a><a href="/shopping/women/shoes/items">Обувь</a><a href="/shopping/women/jewellery/items">Украшения</a></div>
                        <div><h3>Для него</h3><a href="/shopping/men/clothing/items">Одежда</a><a href="/shopping/men/shoes/items">Обувь</a><a href="/shopping/men/bags/items">Сумки</a><a href="/shopping/men/watches/items">Часы</a></div>
                    </div>
                </div>
            </section>
        </main>
        <Footer />
        </>
    );
};

export default CommonHome;
